<template>
  <transition v-if="showModal" name="product-list-modal-transition">
    <Modal :show="showModal" @update:show="$emit('update:show', $event)" class="product-list-modal">
      <div class="modal-header">
        <div class="modal-title">
          <p>{{ roomIdeasText }}</p>
        </div>
        <div class="close-modal-button">
          <span class="af af-close" @click="closeModal"></span>
        </div>
        <div class="num-products-text">{{ numProducts }}</div>
      </div>
      <div class="modal-content">
        <div v-for="product in activeProducts" :key="product.id" class="room-ideas-product" @click="track">
          <Product :product="product" :show-title="true">
            <p class="product-title">{{ product.title }}</p>
            <p class="product-price">{{ product.price }}</p>
          </Product>
        </div>
      </div>
    </Modal>
  </transition>
</template>

<script>
import { Modal, MediaUtils, Image } from '@/pixel/index';
import LocationService from '@/services/LocationService';
import CountryUtils from '@/components/commons/CountryUtils.js';
import { formatMoney } from '@/components/cart/utils.js';
import Product from '@/components/commons/Product.vue';
import Tracking from '@/components/commons/Tracking.js';
import useHome from '@/compositions/useHome';
import useCommon from '@/compositions/useCommon';

export default {
  name: 'ProductListModal',
  components: {
    Modal,
    'responsive-image': Image,
    Product,
  },
  data() {
    return {
      activeRoom: '',
      currentProducts: [],
    };
  },
  setup() {
    const { currentRoom, activeFileProducts, roomIdeasTracking } = useCommon();

    const { showModal, updateShowProductListModal } = useHome();

    return {
      currentRoom,
      activeFileProducts,
      roomIdeasTracking,
      showModal,
      updateShowProductListModal,
    };
  },
  computed: {
    numProducts() {
      return `${this.activeFileProducts?.length} Products`;
    },

    roomIdeasText() {
      let parts = this.activeRoom.split('-');
      parts = parts.map((str) => str.charAt(0).toUpperCase() + str.substring(1));
      return parts.join(' ') + ' Ideas';
    },

    activeProducts() {
      const prods = this.currentProducts.map((product) => {
        const prod = product.attributes;
        return {
          id: +product.id,
          url: prod.url,
          productImage: {
            url: `${prod.imageUrl}?fit=max&w={width}&q={q}`,
          },
          skuNo: prod.skuNo,
          title: prod.title,
          isOnSale: this.hasSalePrice(prod.salePrice),
          originalPrice: this.getPrice(prod.basePrice),
          price: this.hasSalePrice(prod.salePrice) ? this.getPrice(prod.salePrice) : this.getPrice(prod.basePrice),
        };
      });
      return prods;
    },
  },
  watch: {
    currentRoom: {
      handler(room) {
        if (room) {
          this.activeRoom = room;
        }
      },
    },
    activeFileProducts: {
      handler(prods) {
        if (prods) {
          this.currentProducts = prods;
        }
      },
    },
  },
  beforeMount() {
    if (!!this.currentProducts) {
      this.currentProducts = this.activeFileProducts;
    }
  },
  mounted() {
    window.addEventListener('resize', this.hideProductListModal);
  },
  unmounted() {
    window.removeEventListener('resize', this.hideProductListModal);
  },
  methods: {
    closeModal() {
      this.updateShowProductListModal(false);
    },
    hideProductListModal() {
      if (MediaUtils.isMobileAndSmaller) return;
      this.updateShowProductListModal(false);
    },
    hasSalePrice(obj) {
      return obj[LocationService.getCurrencyId()] != null;
    },
    getPrice(obj) {
      return this.formatCurrency(obj[LocationService.getCurrencyId()]);
    },
    formatCurrency(amount) {
      return formatMoney(CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol, amount);
    },
    track() {
      const tracked = this.roomIdeasTracking;
      if (!tracked) return;
      Tracking.track('image_tagging_product_click', {
        channel: 'MODAL',
        image_number: tracked.index,
        room: tracked.room,
        room_images_num: tracked.roomImagesNum,
        id: tracked.file,
        variant: tracked.variant,
      });
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.modal.product-list-modal-transition-enter-active,
.modal.product-list-modal-transition-leave-active {
  transition: opacity 150ms ease-out;
}

.modal.product-list-modal-transition-enter-from,
.modal.product-list-modal-transition-leave-to {
  opacity: 0;
}

.product-list-modal {
  background: white;
  z-index: 999;
  align-items: unset;
  width: 100vw;
  overflow: hidden !important !important;
  .modal-header {
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
    box-shadow: inset 0px -1px 0px #f2f2f2;
    height: 56px;
    position: fixed;
    background: $white;
    top: 0px;
    left: 0px;
    width: 100vw;
    z-index: 99;
    div {
      margin-left: 24px;
    }
    .modal-title {
      margin-top: 4px;
    }
    div:first-child {
      height: 18px;
      p {
        font-size: 14px;
        display: inline-block;
      }
    }
    .close-modal-button {
      position: absolute;
      top: 16px;
      right: 22px;
      font-size: 16px;
      width: 24px;
      height: 24px;
      /* purgecss ignore */
      &:hover {
        cursor: pointer;
      }
    }
    div.num-products-text {
      font-weight: 600;
      height: 20px;
      margin-bottom: 4px;
    }
  }
  .modal-content {
    margin-top: 60px;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 24px;
    .room-ideas-product {
      margin-bottom: 24px;
      text-align: center;
      img {
        margin: 0 auto;
        margin-bottom: 8px;
      }
      .product-title {
        margin-bottom: 0px;
        /* purgecss ignore */
        &:hover {
          cursor: pointer;
        }
      }
      .product-price {
        color: $coral;
      }
    }
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
}
.modal.product-list-modal {
  .body {
    padding: unset;
  }
}
</style>
